import { computed, type Ref } from 'vue'
import type { QuestionGroup } from '../models/question-group'
import type CreatableFeedback from '../models/creatable-feedback'
import { useWysiwygPlainText } from '@/core/composables/use-wysiwyg-plain-text'
import type { Scale } from '@/core/models/scale'

export type FinishFeedbackQuestion = {
  id: string
  questionGroupId: string
  content: string
  scale: Scale | undefined
  feedbackValue: number | undefined
  feedbackText: string | undefined
}

export const useFinishFeedbackSummary = (
  questionGroups: Ref<ReadonlyArray<QuestionGroup>>,
  feedbacks: Ref<Omit<CreatableFeedback, 'feedbackReceiverId'>[]>,
) => {
  const { getPreview } = useWysiwygPlainText()

  const allQuestions = computed(() =>
    questionGroups.value.flatMap((questionGroup) => questionGroup.questions),
  )
  const numberOfQuestions = computed(() => allQuestions.value.length)
  const numberOfFeedbacks = computed(
    () =>
      feedbacks.value.filter(
        (feedback) => feedback.value != undefined || feedback.text != undefined,
      ).length,
  )
  const percentage = computed(() =>
    numberOfQuestions.value === 0
      ? 0
      : (numberOfFeedbacks.value / numberOfQuestions.value) * 100,
  )

  const questionsWithFeedback = computed((): FinishFeedbackQuestion[] =>
    questionGroups.value.flatMap((questionGroup) => {
      return questionGroup.questions.map((question) => {
        const feedback = feedbacks.value.find((feedback) => feedback.questionId == question.id)
        return {
          id: question.id,
          questionGroupId: questionGroup.id,
          content: question.content,
          scale: questionGroup.scale,
          feedbackValue: feedback?.value,
          feedbackText: feedback?.text ? getPreview(feedback.text) : undefined,
        }
      })
    }),
  )

  const lastQuestion = computed(() => {
    const lastGroup = questionGroups.value[questionGroups.value.length - 1]
    const lastQuestionInGroup = lastGroup.questions[lastGroup.questions.length - 1]
    return {
      questionId: lastQuestionInGroup.id,
      questionGroupId: lastGroup.id,
    }
  })

  return {
    numberOfQuestions,
    numberOfFeedbacks,
    percentage,
    questionsWithFeedback,
    lastQuestion,
  }
}
